import React, { useEffect, useMemo, useState, useRef } from 'react'
import { Box, Snackbar, Alert } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyFilter from '../common/KeyFilter'
import ReusableTable from './ReusableTable'
import AddSection from './AddSection'
import { sectionMasterTableConfig } from '../configs/sectionMasterConfig'
import actionManager from '../common/actionManagerInstance'
import {
  applicableToListApi,
  payeeStatusApi,
  createSectionMasterApi,
  searchSectionMasterApi,
} from '../../../../api/SectionMasterApi'
import { toDDMMYYYY } from '../../../../constants/Utils'
import Search from '../../../common/Search'
import ConfirmationDialog from '../../../common/ConfirmationDialog'
import {  getValidCriteria } from '../../../../constants/index.js'
import {z} from "zod";


const SectionMaster = ({ roleConfig }) => {
  const [gapDialogOpen, setGapDialogOpen] = useState(false)
  const [pendingStartDateChange, setPendingStartDateChange] = useState(null)
  const defaultGapMessage =
    'There is a gap between the "End Date" & "Start Date" for which there will be no applicability of section & broadhead. Do you want to continue?'
  const [gapDialogTitle, setGapDialogTitle] = useState(
    'Gap detected between dates'
  )
  const [gapDialogMessage, setGapDialogMessage] = useState(defaultGapMessage)
  const gapConfirmActionRef = useRef(null)
  const [loading, setLoading] = useState(false)

  const [rawSectionMasterList, setRawSectionMasterList] = useState([])
  const [selectedTanMasterUuid, setSelectedTanMasterUuid] = useState('')
  const [showTable, setShowTable] = useState(false)
  const [tableData, setTableData] = useState([])
  const [showAddSection, setShowAddSection] = useState(false)
  const [addSectionFormData, setAddSectionFormData] = useState({})
  const [addSectionResetKey, setAddSectionResetKey] = useState(0)
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [editingRowId, setEditingRowId] = useState(null)
  const [editingData, setEditingData] = useState({})
  const [selectedRows, setSelectedRows] = useState([])
  const [validationErrors, setValidationErrors] = useState({})

  const [applicableToOptions, setApplicableToOptions] = useState([])
  const [payeeStatusOptions, setPayeeStatusOptions] = useState([])

  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const searchIconRef = useRef(null)
  const [searchCriteria, setSearchCriteria] = useState([
    { id: 1, logicalOperator: 'Where', field: 'section', value: '' },
  ])
  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)

  const ddMMyyyyListZod = z
  .string()
  .regex(
    /^((0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}(\s*,\s*(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4})*)?$/,
    'Invalid Input. Example: 29/08/2025, 01/09/2025'
  )

   const tableColumns = useMemo(
    () =>
      sectionMasterTableConfig.columns.map(col => {
        if (col.key === 'payeeStatus')
          return {
            ...col,
            editable: true,
            type: 'multiSelect',
            options: payeeStatusOptions,
          }
        if (col.key === 'startDate' || col.key === 'endDate')
          return { ...col, editable: true, type: 'date' }
        if (
          [
            'thresholdAnnual',
            'thresholdTransaction',
            'prescribedRate',
          ].includes(col.key)
        )
          return { ...col, editable: true, type: 'text' }
        return col
      }),
    [payeeStatusOptions]
  )
  
    // Search fields constant (add Load ID and all columns)
  const searchFields = [
    ...tableColumns.map(col => {
      if (col.key === 'startDate' || col.key === 'endDate') {
        return {
          id: col.key,
          label: col.label,
          type: 'text',
          placeholder: `Example: 25/08/2025 (dd/mm/yyyy)`,
          zod: ddMMyyyyListZod,
        };
      }
       if (col.key === 'section') {
        return {
          id: 'sectionCode',
          label: col.label,
          type: 'text',
          placeholder: `Enter ${col.label}`,
        };
      }
      return {
        id: col.key,
        label: col.label,
        type: col.type || 'text',
        placeholder: `Enter ${col.label}`,
      };
    }),
  ];
  // ------------------ DATE HELPERS ------------------
  const toInputDateFormat = dateStr => {
    if (!dateStr) return ''
    const [d, m, y] = dateStr.split(/[\/-]/)
    if (!d || !m || !y) return ''
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }

  const fromInputDateFormat = dateStr => {
    if (!dateStr) return ''
    const [y, m, d] = dateStr.split('-')
    if (!y || !m || !d) return ''
    return `${d}/${m}/${y}`
  }

  const parseDDMMYYYY = dateStr => {
    if (!dateStr) return null
    const [d, m, y] = String(dateStr).split(/[\/-]/)
    const dd = parseInt(d, 10),
      mm = parseInt(m, 10),
      yy = parseInt(y, 10)
    if (!dd || !mm || !yy) return null
    return new Date(yy, mm - 1, dd)
  }

  // ------------------ REUSABLE FETCH FUNCTION ------------------
  const fetchSectionMasterList = async (
    page = 1,
    pageSize = itemsPerPage,
    tanUuid = selectedTanMasterUuid,
    searchCOList = []
  ) => {
    setLoading(true)
    try {
      const searchRes = await searchSectionMasterApi(
        {
          pageFilter: {
            currentPage: page,
            includeCount: true,
            pageSize: pageSize,
            sortField: 'sectionCode',
            sortOrder: 'ASC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList,
        },
        tanUuid
      )

      const apiData = searchRes?.data
      const list =
        apiData?.data?.list ||
        apiData?.list ||
        apiData?.data?.content ||
        apiData?.content ||
        []
      setRawSectionMasterList(list)

      const mappedRows = list.map(created => ({
        section: created.section ?? created.sectionCode ?? '',
        broadHead: created.broadHead ?? '',
        startDate: created.startDate ?? '',
        endDate: created.endDate ?? '',
        applicableTo: created.applicableTo ?? '',
        payeeStatus: created.payeeStatus ?? '',
        thresholdAnnual: created.thresholdAnnual ?? 0,
        thresholdTransaction: created.thresholdTransaction ?? 0,
        prescribedRate: created.prescribedRate ?? 0,
        sectionMasterUuid: created.uuid || '',
        globalSectionMaster: created.globalSectionMaster || false,
        associateSectionMaster: created.associateSectionMaster || false,
      }))
      
      setTotalItems(apiData?.totalElements)
      setTableData(mappedRows)
      setShowTable(true)

      return list
    } catch (e) {
      console.error('Failed to fetch Section Master data')
      setNotification({
        open: true,
        message: 'Failed to fetch Section Master data',
        severity: 'error',
      })
      return []
    } finally {
      setLoading(false)
    }
  }

  // ------------------ INITIAL DROPDOWNS ------------------
  useEffect(() => {
    if (!roleConfig?.canCreate) return
    let isMounted = true
    const normalizeOptions = resp => {
      const list = resp?.data?.data || resp?.data?.list || resp?.data || []
      if (!Array.isArray(list)) return []
      return list.map(item => {
        if (typeof item === 'string') return { value: item, label: item }
        const value = item.value ?? item.code ?? item.name ?? item.label
        const label =
          item.label ??
          item.name ??
          item.value ??
          item.code ??
          String(value ?? '')
        return { value, label }
      })
    }

    ;(async () => {
      try {
        const [appRes, payeeRes] = await Promise.all([
          applicableToListApi(),
          payeeStatusApi(),
        ])
        if (isMounted) {
          setApplicableToOptions(normalizeOptions(appRes))
          setPayeeStatusOptions(normalizeOptions(payeeRes))
        }
      } catch (e) {
        console.error('Failed to initialize Section Master')
        setNotification({
          open: true,
          message: 'Failed to initialize Section Master data',
          severity: 'error',
        })
      }
    })()
    return () => {
      isMounted = false
    }
  }, [roleConfig?.canCreate])

  // ------------------ HANDLERS ------------------
  const handleTanSelect = ({ tanMasterUuid }) =>
    setSelectedTanMasterUuid(tanMasterUuid || '')

  const handleFieldBlur = (rowId, field, value) => {
    if (field !== 'startDate') return
    const row = tableData.find(r => r.sectionMasterUuid === rowId)
    if (!row) return

    const sec = String(row.section ?? '')
      .trim()
      .toLowerCase()
    const bh = String(row.broadHead ?? '')
      .trim()
      .toLowerCase()
    const candidates = (
      Array.isArray(rawSectionMasterList) ? rawSectionMasterList : []
    ).filter(it => {
      const gSec = String(it.section ?? it.sectionCode ?? '')
        .trim()
        .toLowerCase()
      const gBh = String(it.broadHead ?? '')
        .trim()
        .toLowerCase()
      return gSec === sec && gBh === bh
    })
    if (!candidates.length) return

    const newStart = parseDDMMYYYY(value)
    if (!newStart) return

    const prevEnds = candidates
      .map(it => parseDDMMYYYY(it.endDate))
      .filter(d => d instanceof Date && !isNaN(d))
    if (!prevEnds.length) return

    const before = prevEnds.filter(d => d < newStart).sort((a, b) => b - a)
    if (!before.length) return

    const prevEnd = before[0]
    const expected = new Date(
      prevEnd.getFullYear(),
      prevEnd.getMonth(),
      prevEnd.getDate() + 1
    )
    const sameDay =
      newStart.getFullYear() === expected.getFullYear() &&
      newStart.getMonth() === expected.getMonth() &&
      newStart.getDate() === expected.getDate()
    if (!sameDay) {
      setPendingStartDateChange({ rowId, value })
      setGapDialogOpen(true)
    }
  }

  const handleViewClick = async ({ tanMasterUuid }) => {
    setSelectedTanMasterUuid(tanMasterUuid || '')
    setEditingRowId(null)
    setEditingData({})
    setValidationErrors({})
    setSelectedRows([])
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: 'sectionCode',
        value: '',
      },
    ])
    await fetchSectionMasterList(currentPage,itemsPerPage,tanMasterUuid || '', [])
  }

  const handleCloseAddSection = () => {
    setShowAddSection(false)
    setAddSectionFormData({})
    setAddSectionResetKey(k => k + 1)
  }

  const handleSaveSection = async formValues => {
    setLoading(true)
    try {
      const payload = formValues || addSectionFormData
      const requiredFields = [
        'section',
        'broadHead',
        'startDate',
        'applicableTo',
        'payeeStatus',
        'thresholdAnnual',
        'thresholdTransaction',
        'prescribedRate',
      ]
      const missing = requiredFields.filter(f => !payload[f])
      if (missing.length) {
        setNotification({
          open: true,
          message: `Please fill in required fields: ${missing.join(', ')}`,
          severity: 'error',
        })
        return
      }

      const hasOpenEnded = (
        Array.isArray(rawSectionMasterList) ? rawSectionMasterList : []
      ).some(it => {
        const sec = String(it.section ?? it.sectionCode ?? '')
          .trim()
          .toLowerCase()
        const bh = String(it.broadHead ?? '')
          .trim()
          .toLowerCase()
        const noEnd =
          it.endDate == null ||
          String(it.endDate).trim() === '' ||
          String(it.endDate).trim() === '-'
        return (
          sec === String(payload.section).trim().toLowerCase() &&
          bh === String(payload.broadHead).trim().toLowerCase() &&
          noEnd
        )
      })

      if (hasOpenEnded) {
        setNotification({
          open: true,
          message: 'Section exists without End Date. Please provide End Date.',
          severity: 'error',
        })
        return
      }

      const formattedEndDate = payload.endDate?.includes('-')
        ? toDDMMYYYY(payload.endDate)
        : payload.endDate

      const apiBody = {
        sectionCode: payload.section,
        broadHead: payload.broadHead,
        startDate: toDDMMYYYY(payload.startDate),
        endDate: formattedEndDate || null,
        applicableTo: payload.applicableTo,
        payeeStatus: payload.payeeStatus,
        thresholdAnnual: payload.thresholdAnnual || 0,
        thresholdTransaction: payload.thresholdTransaction || 0,
        prescribedRate: payload.prescribedRate || 0,
        globalSectionMaster: false,
        associateSectionMaster: false,
        tanUuid: selectedTanMasterUuid || null,
      }

      const res = await createSectionMasterApi(apiBody)
      if (!res?.success)
        throw new Error(res?.error || 'Failed to create section')

      await fetchSectionMasterList(currentPage,itemsPerPage,selectedTanMasterUuid, [])
      setNotification({
        open: true,
        message: 'Section added successfully!',
        severity: 'success',
      })
      handleCloseAddSection()
    } catch (e) {
      console.error('Create Section Master failed:')
      setNotification({
        open: true,
        message: e.message || 'Failed to add section',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddSectionFormChange = fieldName => event =>
    setAddSectionFormData(prev => ({
      ...prev,
      [fieldName]: event.target.value,
    }))

  const handleCloseNotification = () =>
    setNotification(prev => ({ ...prev, open: false }))

  // ------------------ SEARCH ------------------
  const openSearchDialog = () => {
    setSearchOpen(true)
    setAnchorEl(searchIconRef.current)
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const applySearch = async searchList => {
    setSearchCriteria(
      searchList.map((item, idx) => ({
        ...item,
        field: item.fieldName || item.field || '',
        id: idx + 1,
        logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
      }))
    )
    await fetchSectionMasterList(currentPage,itemsPerPage,selectedTanMasterUuid, searchList)
    setSearchOpen(false)
  }

  const resetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: 'sectionCode',
        value: '',
      },
    ])
    await fetchSectionMasterList(currentPage,itemsPerPage,selectedTanMasterUuid, [])
    setSearchOpen(false)
  }

  // ------------------ TABLE ------------------
  const tableActions = actionManager.getTableActions(
    'sectionMaster',
    {
      selectedRows,
      setSelectedRows,
      tableData,
      setTableData,
      showAddSection: setShowAddSection,
      addSectionFormData,
      setAddSectionFormData,
      setNotification,
      editingRowId,
      setEditingRowId,
      editingData,
      setEditingData,
      selectedTanMasterUuid,
      rawSectionMasterList,
      toDDMMYYYY,
      setValidationErrors,
      searchOpen,
      setSearchOpen,
      anchorEl,
      setAnchorEl,
      searchCriteria,
      setSearchCriteria,
      searchFields,
      openSearchDialog,
      closeSearchDialog,
      applySearch,
      resetSearch,
      searchIconRef,
      setLoading,
      setRawSectionMasterList,
      openGapConfirm: (message, onProceed) => {
        setGapDialogTitle('Gap detected between dates')
        setGapDialogMessage(message || defaultGapMessage)
        gapConfirmActionRef.current = onProceed || null
        setGapDialogOpen(true)
      },
    },
    roleConfig
  )

  const tableActionsWithCancel = useMemo(
    () => [
      ...tableActions,
      {
        type: 'cancel',
        label: 'Cancel',
        tooltip: 'Cancel',
        icon: <CloseIcon />,
      },
    ],
    [tableActions]
  )

  const handleCancelEdit = async () => {
    setEditingRowId(null);
    setEditingData({});
    setValidationErrors({});
    setSelectedRows([]);

    try {
        // Call API to refresh table
        await fetchSectionMasterList(currentPage,itemsPerPage,selectedTanMasterUuid || '', []);
    } catch (e) {
        setNotification({
            open: true,
            message: 'Failed to refresh Section Master data',
            severity: 'error',
        });
    }
};

  

  const handleEditFieldChange = (rowId, field, value) => {
    let newValue = value
    if (['startDate', 'endDate'].includes(field) && typeof value === 'string') {
      newValue = /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? fromInputDateFormat(value)
        : value.replace(/-/g, '/')
    }
    setTableData(prev =>
      prev.map(row =>
        row.sectionMasterUuid === rowId ? { ...row, [field]: newValue } : row
      )
    )

    if (selectedRows.length !== 1) {
      setEditingData(prev => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [field]: newValue },
      }))
    }
  }

  const handlePageChange = page => {
      setCurrentPage(page)
      fetchSectionMasterList(page, itemsPerPage, selectedTanMasterUuid,getValidCriteria(searchCriteria))
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    fetchSectionMasterList(1, newItemsPerPage, selectedTanMasterUuid,getValidCriteria(searchCriteria))
  }
  

  return (
    <Box>
      <KeyFilter
        handleViewClick={handleViewClick}
        onTanSelect={handleTanSelect}
      />

      {showTable && (
        <>
          <ReusableTable
            title={sectionMasterTableConfig.title}
            columns={tableColumns}
            data={tableData}
            actions={tableActionsWithCancel}
            showCheckbox
            maxHeight={500}
            componentName="SectionMaster"
            selectedRows={selectedRows}
            onSelectedRowsChange={setSelectedRows}
            editingRowId={editingRowId}
            editingData={editingData}
            validationErrors={validationErrors}
            onEditFieldChange={handleEditFieldChange}
            onFieldBlur={handleFieldBlur}
            onCancelEdit={handleCancelEdit}
            toInputDateFormat={toInputDateFormat}
            loading={loading}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />

          <ConfirmationDialog
            open={gapDialogOpen}
            onClose={() => setGapDialogOpen(false)}
            onConfirm={() => {
              setGapDialogOpen(false)
              setPendingStartDateChange(null)
              if (gapConfirmActionRef.current) {
                const run = gapConfirmActionRef.current
                gapConfirmActionRef.current = null
                run()
              }
            }}
            type="warning"
            title={gapDialogTitle}
            message={gapDialogMessage}
            confirmText="Proceed"
            itemType=""
          />
        </>
      )}

      <AddSection
        key={addSectionResetKey}
        open={showAddSection}
        onClose={handleCloseAddSection}
        onSave={handleSaveSection}
        formData={addSectionFormData}
        onChange={handleAddSectionFormChange}
        applicableToOptions={applicableToOptions}
        payeeStatusOptions={payeeStatusOptions}
        existingSections={rawSectionMasterList}
        onNotify={({ message, severity = 'info' }) =>
          setNotification({ open: true, message, severity })
        }
        roleConfig={roleConfig}
      />

      <Search
        open={searchOpen}
        anchorEl={anchorEl}
        fields={searchFields}
        searchCriteria={searchCriteria}
        setSearchCriteria={setSearchCriteria}
        onClose={closeSearchDialog}
        onApply={applySearch}
        onReset={resetSearch}
      />


      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SectionMaster
